import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Heart, Globe, Shield, Sparkles, Target, Users } from 'lucide-react';

const About = () => {
  const values = [
    { 
      icon: <Target size={24} />, 
      title: "Precision Medicine", 
      description: "We tailor treatment recommendations to each patient by looking at the full picture of their health data, because no two people, or cancers, are the same.",
      color: "#3b82f6"
    },
    { 
      icon: <Sparkles size={24} />, 
      title: "Innovation First", 
      description: "We bring the latest in technology together with medical expertise to create smarter, faster, and more accurate support for cancer care.",
      color: "#8b5cf6"
    },
    { 
      icon: <Users size={24} />, 
      title: "Collaborative Care", 
      description: "We work hand-in-hand with oncologists, giving them clear, evidence-based insights that help them make the best decisions for their patients.",
      color: "#06b6d4"
    }
  ];

  return (
    <AboutSection id="about">
      <BackgroundPattern />
      <ContentWrapper>
        {/* Values Section */}
        <ValuesSection>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <ValuesTitle>Our Core Principles</ValuesTitle>
            <ValuesDescription>
              Driven by innovation, guided by compassion, and committed to excellence in patient care.
            </ValuesDescription>
          </motion.div>
          
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <IconContainer style={{ backgroundColor: `${value.color}15`, color: value.color }}>
                  {value.icon}
                </IconContainer>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ValuesSection>
      </ContentWrapper>
    </AboutSection>
  );
};

const AboutSection = styled.section`
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 80px 0;
  position: relative;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(147, 51, 234, 0.1) 0%, transparent 50%);
  background-size: 50px 50px, 50px 50px, 100% 100%, 100% 100%;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent);
  }
`;

const SectionLabel = styled.div`
  display: inline-block;
  color: #3b82f6;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
`;

const MainTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #1a1a1a;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #2094F1 0%, #856AC3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const MainDescription = styled.p`
  font-size: 1.3rem;
  line-height: 1.7;
  color: #64748b;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const StatsSection = styled.div`
  margin-bottom: 80px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -40px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.3), transparent);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 30px;
  max-width: 900px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    max-width: 300px;
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 30px 15px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
    background: rgba(255, 255, 255, 0.8);
  }
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ValuesSection = styled.div`
  text-align: center;
  position: relative;
  padding-top: 40px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2), transparent);
  }
`;

const ValuesTitle = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1a1a1a;
`;

const ValuesDescription = styled.p`
  font-size: 1.2rem;
  color: #64748b;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const ValueCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  padding: 40px 30px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.9);
    
    &::before {
      left: 100%;
    }
  }
`;

const IconContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 24px;
`;

const ValueTitle = styled.h4`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a1a1a;
`;

const ValueDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
  font-size: 1rem;
`;

export default About;